/**
 * Cases this subject is *relevant to* — and nothing stronger than that.
 *
 * **The wording is the feature.** A cross-reference says one sentence: this research
 * subject is relevant to this case. It does not say the research was carried out under the
 * case's attestation, and no evidence is shared in either direction. Subject research keeps
 * the purpose and legal basis of the record it was gathered under; case evidence remains
 * what the case collected. So this panel never says "case evidence", "inherited context" or
 * "added to case", and it prints the sentence the row actually asserts.
 *
 * **Where the two attestations differ, the screen asks a person.** Purposes and Article 6
 * bases are vocabularies rather than scales — `consent` is not stricter than
 * `legitimate_interest` — so nothing here ranks them or picks one. The API refuses, and the
 * form that appears asks the researcher to attest for the association themselves, which is
 * a decision recorded against their name and immutable once made.
 */

import { useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request } from "@/lib/api";
import { Badge, Button, EmptyState, ErrorState, Loading } from "@/components/ui";
import { useCan } from "@/hooks/usePermissions";
import type { CrossReferenceCaseRequest } from '@/lib/schemas';

interface CrossReference {
  case_id: string;
  relationship: string;
  meaning: string;
  linked_at: string;
  attested_for_association: boolean;
  investigation_purpose: string | null;
  legal_basis: string | null;
  authorization_attestation: string | null;
  attested_at: string | null;
}

interface CaseRow {
  id: string;
  reference: string;
  title: string;
}

const PURPOSES = [
  "due_diligence",
  "kyc",
  "fraud_investigation",
  "cybersecurity",
  "journalism",
  "brand_protection",
  "self_footprint",
] as const;

const BASES = [
  "legitimate_interest",
  "legal_obligation",
  "consent",
  "contract",
  "public_task",
  "vital_interests",
] as const;

/**
 * The related-cases panel.
 *
 * @param props - The subject to read.
 * @returns The panel.
 */
export function SubjectCasesPanel({ subjectId }: { subjectId: string }): ReactElement {
  const canEdit = useCan("create_run_cases");
  const queryClient = useQueryClient();
  const [caseId, setCaseId] = useState("");
  const [purpose, setPurpose] = useState("");
  const [basis, setBasis] = useState("");
  const [attestation, setAttestation] = useState("");
  const [needsAttestation, setNeedsAttestation] = useState(false);

  const links = useQuery({
    queryKey: ["subject-cases", subjectId],
    queryFn: () => request<CrossReference[]>(`/research/subjects/${subjectId}/cases`),
  });

  const cases = useQuery({
    queryKey: ["cases"],
    queryFn: () => request<{ data: CaseRow[] }>("/cases?limit=50"),
  });

  const link = useMutation({
    mutationFn: () =>
      request<CrossReference, CrossReferenceCaseRequest>(`/research/subjects/${subjectId}/cases`, {
        method: "POST",
        body: needsAttestation
          ? {
              case_id: caseId,
              investigation_purpose: purpose,
              legal_basis: basis,
              authorization_attestation: attestation,
            }
          : { case_id: caseId },
      }),
    onSuccess: () => {
      setCaseId("");
      setNeedsAttestation(false);
      setAttestation("");
      void queryClient.invalidateQueries({ queryKey: ["subject-cases", subjectId] });
    },
    onError: () => {
      // The API refuses when the attestations differ, and the refusal is the prompt: the
      // researcher is being asked to take responsibility, not told something went wrong.
      setNeedsAttestation(true);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      request<void>(`/research/subjects/${subjectId}/cases/${id}`, { method: "DELETE" }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["subject-cases", subjectId] }),
  });

  if (links.isLoading) return <Loading label="Loading related cases" />;
  if (links.error != null) return <ErrorState error={links.error} />;

  const rows = links.data ?? [];

  return (
    <section className="space-y-3" aria-label="Related cases panel" data-testid="subject-panel-cases">
      <p className="max-w-2xl text-xs text-muted">
        Cases this person is <strong>relevant to</strong>. A cross-reference shares no
        evidence: the research here was not carried out under the case&apos;s attestation,
        and nothing is copied into the case or out of it.
      </p>

      {canEdit && (
        <div className="space-y-2 rounded border border-line p-3">
          <label className="block text-xs text-muted">
            Cross-reference to a case
            <select
              className="mt-1 w-full rounded border border-line bg-surface p-2 text-sm"
              data-testid="crossref-case"
              value={caseId}
              onChange={(event) => setCaseId(event.target.value)}
            >
              <option value="">Choose a case…</option>
              {(cases.data?.data ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.reference} — {row.title}
                </option>
              ))}
            </select>
          </label>

          {needsAttestation && (
            <div className="space-y-2" data-testid="crossref-attestation">
              <p className="text-xs text-muted">
                This subject&apos;s evidence was gathered under an attestation that differs
                from this case&apos;s. <strong>No precedence is assigned between purposes or
                legal bases</strong> — they are different obligations, not a scale. To
                cross-reference anyway, attest for the association yourself. Your name and
                the moment are recorded, and the attestation cannot be edited afterwards.
              </p>
              <select
                className="w-full rounded border border-line bg-surface p-2 text-sm"
                data-testid="crossref-purpose"
                value={purpose}
                onChange={(event) => setPurpose(event.target.value)}
              >
                <option value="">Investigation purpose…</option>
                {PURPOSES.map((value) => (
                  <option key={value} value={value}>
                    {value.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
              <select
                className="w-full rounded border border-line bg-surface p-2 text-sm"
                data-testid="crossref-basis"
                value={basis}
                onChange={(event) => setBasis(event.target.value)}
              >
                <option value="">Legal basis…</option>
                {BASES.map((value) => (
                  <option key={value} value={value}>
                    {value.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
              <textarea
                rows={2}
                className="w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
                data-testid="crossref-attestation-text"
                placeholder="What authority are you relying on for this association?"
                value={attestation}
                onChange={(event) => setAttestation(event.target.value)}
              />
            </div>
          )}

          <Button
            tone="primary"
            data-testid="crossref-save"
            disabled={
              caseId === "" ||
              link.isPending ||
              (needsAttestation && (purpose === "" || basis === "" || attestation.trim() === ""))
            }
            onClick={() => link.mutate()}
          >
            {link.isPending ? "Saving…" : needsAttestation ? "Attest and cross-reference" : "Cross-reference"}
          </Button>
          {link.error != null && <ErrorState error={link.error} />}
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState>Not related to any case.</EmptyState>
      ) : (
        <ul className="space-y-2" data-testid="crossref-list">
          {rows.map((row) => (
            <li
              key={row.case_id}
              className="space-y-1 rounded border border-line p-3 text-sm"
              data-testid="crossref-row"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="muted">Related case</Badge>
                <span className="font-mono text-xs text-muted">{row.case_id.slice(0, 8)}…</span>
                {row.attested_for_association && (
                  <Badge tone="warn">attested for this link</Badge>
                )}
                {canEdit && (
                  <button
                    type="button"
                    className="ml-auto text-xs text-muted hover:text-danger"
                    data-testid="crossref-remove"
                    onClick={() => remove.mutate(row.case_id)}
                  >
                    Withdraw
                  </button>
                )}
              </div>
              <p className="text-xs text-muted">{row.meaning}</p>
              {row.attested_for_association && (
                <p className="text-xs text-muted">
                  Attested {row.investigation_purpose?.replaceAll("_", " ")} /{" "}
                  {row.legal_basis?.replaceAll("_", " ")} — {row.authorization_attestation}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
